import type { AdminEventFeedbackResponse } from '@mds/shared/types/event-feedback';
import { answerText } from '@mds/shared/lib/form-answers';
import { FORM_RATING_MAX } from '@mds/shared/types/form-question';

import * as feedback from '@/events/feedback.api';
import { download, stamp } from '@/lib/export';

/**
 * Every feedback response for one event as an Excel workbook: one row per
 * response, one column per question. Anonymous like the screen it comes from.
 *
 * Built in the browser, so `exceljs` is loaded only when someone exports and
 * never weighs on the admin bundle.
 */

/* The brand palette as ARGB, traceable to `frontend/shared/src/styles/primitives.css`
 * and matching the analytics workbook (`backend/src/modules/admin-reporting/report-export.ts`).
 * Excel has no tokens, so the literals live here, named for the primitive they copy. */
const MINT_DARK = 'FF168B70'; // --mint-700
const INK_SECONDARY = 'FF5B6472'; // --neutral-500
const HAIRLINE = 'FFDDE3EA'; // --line-100
const WHITE = 'FFFFFFFF';

/** The API's page cap for this list. */
const PAGE_LIMIT = 100;

/** Rows above the table: title, details, blank. */
const HEADER_ROW = 4;

async function fetchAll(eventId: string) {
  const rows: AdminEventFeedbackResponse[] = [];
  for (let page = 1; ; page += 1) {
    const result = await feedback.listResponses(eventId, {
      page,
      limit: PAGE_LIMIT,
      direction: 'asc',
    });
    rows.push(...result.data);
    if (page >= result.pagination.totalPages) return { rows, summary: result.summary };
  }
}

/** Excel stores wall-clock time, so shift the instant to the admin's own clock. */
const localWallClock = (iso: string) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
};

export async function exportFeedbackXlsx(eventId: string, eventTitle: string) {
  const [{ default: ExcelJS }, { rows, summary }] = await Promise.all([
    import('exceljs'),
    fetchAll(eventId),
  ]);

  // Questions in the order they first appear, so a form edited mid-way still
  // gets one column per question, labelled as guests saw it.
  const questions = new Map<string, string>();
  for (const r of rows)
    for (const a of r.answers) {
      if (!questions.has(a.questionId)) questions.set(a.questionId, a.label);
    }

  const book = new ExcelJS.Workbook();
  const sheet = book.addWorksheet('Responses', {
    views: [{ state: 'frozen', ySplit: HEADER_ROW }],
  });

  sheet.columns = [
    { key: 'submitted', width: 20 },
    { key: 'rating', width: 10 },
    { key: 'comment', width: 48 },
    ...[...questions.keys()].map((id) => ({ key: id, width: 32 })),
  ];

  const title = sheet.getCell('A1');
  title.value = `${eventTitle} — feedback responses`;
  title.font = { bold: true, size: 16, color: { argb: MINT_DARK } };

  const details = sheet.getCell('A2');
  const average =
    summary.averageOverallRating === null ? '–' : summary.averageOverallRating.toFixed(1);
  details.value =
    `${summary.totalResponses} responses from ${summary.checkedInRegistrations} checked-in ` +
    `parties · ${Math.round(summary.responseRatePercent)}% response rate · ` +
    `average rating ${average} / ${FORM_RATING_MAX} · exported ${stamp()}`;
  details.font = { color: { argb: INK_SECONDARY } };

  const header = sheet.getRow(HEADER_ROW);
  header.values = ['Submitted', 'Rating', 'Comment', ...questions.values()];
  header.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: WHITE } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: MINT_DARK } };
    cell.alignment = { vertical: 'middle', wrapText: true };
  });
  header.height = 30;

  for (const r of rows) {
    const byId = new Map(r.answers.map((a) => [a.questionId, answerText(a.value)]));
    const row = sheet.addRow({
      submitted: localWallClock(r.submittedAt),
      rating: r.overallRating,
      comment: r.comment,
      ...Object.fromEntries([...questions.keys()].map((id) => [id, byId.get(id) ?? ''])),
    });
    row.alignment = { vertical: 'top', wrapText: true };
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = { bottom: { style: 'thin', color: { argb: HAIRLINE } } };
    });
    row.getCell('submitted').numFmt = 'dd/mm/yy h:mm AM/PM';
    row.getCell('rating').alignment = { vertical: 'top', horizontal: 'center' };
  }

  if (rows.length > 0) {
    sheet.autoFilter = {
      from: { row: HEADER_ROW, column: 1 },
      to: { row: HEADER_ROW, column: 3 + questions.size },
    };
  }

  const slug =
    eventTitle
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'event';
  download(
    `${slug}-feedback-${stamp()}.xlsx`,
    await book.xlsx.writeBuffer(),
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
}
